import React, { useState, FC, useEffect } from 'react';
import Modal from 'react-modal';

import { Button } from '@GxT-Team/gxt-components';
import ModalHeader from '../../../../../components/modals/ModalHeader';
import Input from '../../../../../components/elements/Input';
import { AddDataSourceModalCustomStyles } from '../constants';
import { useDataSource } from '../../../../../services/api/dataSource/useDataSourceAPI';
import {
  AddDataSourceModalProps,
  DataSourceFormData,
  FormErrors,
  ProcessVariable,
} from '../../../types';
import { dataSourceSchema } from '../../../schema';

const AddDataSourceModal: FC<AddDataSourceModalProps> = ({
  isOpen,
  closeModal,
  execFunction,
  selectedDataSource,
  setSelectedDataSource,
}) => {
  const initialFormValues: DataSourceFormData = {
    sqlQuery: '',
    name: '',
    type: 'sql',
    val: '',
    uniqueIdentifier: '',
  };
  const [formData, setFormData] = useState<DataSourceFormData>(initialFormValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [tableHeader, setIsTableHeader] = useState<string[]>([]);
  const [processVariables, setIsProcessVariables] = useState<ProcessVariable[]>([]);
  const [tableBody, setIsTableBody] = useState<Record<string, string>[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { processQuery } = useDataSource();

  const btnTilte = selectedDataSource ? 'Update Data Source' : 'Add Data Source';

  useEffect(() => {
    if (selectedDataSource) {
      setFormData({
        sqlQuery: selectedDataSource?.sqlQuery ?? '',
        name: selectedDataSource?.name ?? '',
        type: selectedDataSource?.type ?? 'sql',
        val: '',
        uniqueIdentifier: selectedDataSource?.uniqueIdentifier ?? '',
      });
    }
  }, [selectedDataSource]);

  const inputField = {
    label: 'Data Connection Name',
    type: 'text',
    name: 'name',
    value: formData.name,
    placeholder: 'Enter ',
    inputClassName: 'w-full !font-bold',
  };
  const InputTextArea = {
    label: 'SQL',
    placeholder: 'Enter ',
    type: 'textarea',
    name: 'sqlQuery',
    value: formData.sqlQuery,
    rows: 15,
  };

  const submitData = async (e: React.FormEvent) => {
    e.preventDefault();

    const zodValidationResult = dataSourceSchema.safeParse({
      name: formData.name,
      sqlQuery: formData.sqlQuery,
    });
    if (!zodValidationResult.success) {
      const zodErrors: FormErrors = {};
      zodValidationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          zodErrors[err.path[0]] = err.message;
        }
      });
      setErrors(zodErrors);
      return;
    }
    setErrors({});

    setIsLoading(true);

    try {
      await execFunction(formData);
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      setIsLoading(false);
    }
    closeModalHandler();
  };

  const closeModalHandler = () => {
    setErrors({});
    setFormData(initialFormValues);
    setIsTableHeader([]);
    setIsTableBody([]);
    setSelectedDataSource?.(undefined);
    setIsProcessVariables([]);
    setIsLoading(false);
    closeModal();
  };

  const formDataHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prevValue) => ({ ...prevValue, [e.target.name]: e.target.value }));
  };

  const processData = (e: React.FormEvent) => {
    e.preventDefault();
    setIsTableHeader([]);
    setIsTableBody([]);
    const regex = /{{\s*([^}]+)\s*}}/g;

    let match;
    const result: ProcessVariable[] = [];

    // Use the regex to extract the matches
    while ((match = regex.exec(formData.sqlQuery)) !== null) {
      // match[1] is the part inside {{ }}
      const key = match[1].trim();
      result.push({ [key]: '' });
    }

    setIsProcessVariables(result);
  };

  const previewData = async (e: React.FormEvent) => {
    e.preventDefault();
    formData.val = processVariables;
    setIsTableHeader([]);
    setIsTableBody([]);

    await processQuery
      .mutateAsync(formData)
      .then((res) => {
        if (res?.data?.data[0]) {
          setIsTableHeader(Object.keys(res.data.data[0]));
          setIsTableBody(res.data.data);
        }
      })
      .catch((err) => {
        console.error(err);
        setIsTableHeader([]);
        setIsTableBody([]);
      });
  };

  const processDataHandler = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const updatedVariables = [...processVariables];
    const key = Object.keys(updatedVariables[index])[0];
    const updatedValue = e.target.value;

    updatedVariables[index] = {
      [key]: updatedValue,
    };

    setIsProcessVariables(updatedVariables);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModalHandler}
      style={AddDataSourceModalCustomStyles}
      ariaHideApp={false}>
      <div className="space-y-4 sm:p-8">
        <ModalHeader title={btnTilte} className=" text-4xl !text-[#177ba6]   " />
        <form className="space-y-4" onSubmit={submitData}>
          <div className="flex">
            <div className="w-1/2">
              <label
                htmlFor={InputTextArea.name}
                className={`${
                  errors[inputField.name] ? 'text-red-500' : 'text-gray-900'
                } block mb-2 text-sm font-medium`}>
                {inputField?.label}
              </label>
              <input
                name={inputField.name}
                value={inputField.value}
                placeholder={inputField.placeholder}
                onChange={formDataHandler}
                data-testid={`text-${inputField.name}`}
                className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg p-2.5 placeholder-gray-400 block w-full"
              />
            </div>

            <div className="ml-4 mt-3">
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700">Connection Type</label>
              </div>

              <div className="flex space-x-4">
                <div>
                  <input
                    type="radio"
                    id="api"
                    name="type"
                    value="api"
                    checked={formData.type === 'api'}
                    onChange={formDataHandler}
                  />
                  <label htmlFor="api" className="ml-1">
                    API
                  </label>
                </div>

                <div>
                  <input
                    type="radio"
                    id="sql"
                    name="type"
                    value="sql"
                    checked={formData.type === 'sql'}
                    onChange={formDataHandler}
                  />
                  <label htmlFor="sql" className="ml-1">
                    SQL
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="">
            <span>
              <label
                htmlFor={InputTextArea.name}
                className={`${
                  errors[InputTextArea.name] ? 'text-red-500' : 'text-gray-900'
                } block mb-2 text-sm font-medium`}>
                {InputTextArea?.label}
              </label>
              <textarea
                name={InputTextArea.name}
                value={InputTextArea.value}
                placeholder={InputTextArea.placeholder}
                onChange={formDataHandler}
                data-testid={`text-${InputTextArea.name}`}
                rows={InputTextArea.rows}
                style={{ resize: 'none' }}
                className="w-full p-2 border rounded"
              />
            </span>
          </div>
          <div className="flex !mt-4">
            <Button
              variant="primary"
              onClick={processData}
              disabled={formData?.sqlQuery !== '' ? false : true}
              data-testid="process-btn"
              className=" mr-4  !border-none">
              {`Process`}
            </Button>
            <Button
              variant="primary"
              onClick={previewData}
              disabled={formData?.sqlQuery !== '' ? false : true}
              data-testid="preview-btn"
              className="flex justify-between items-center gap-3 whitespace-nowrap !h-10  !border-none">
              {`Preview Data`}
            </Button>
            {/* {tableHeader.length > 0 && ( */}
            <div className="ml-4">
              <Input
                type="select"
                options={
                  tableHeader.length > 0
                    ? tableHeader.map((header) => ({
                        label: header,
                        value: header,
                      }))
                    : formData.uniqueIdentifier
                      ? [{ label: formData.uniqueIdentifier, value: formData.uniqueIdentifier }]
                      : []
                }
                placeholder="Select a column"
                className="bg-[#177ba6] !text-white"
                name={'uniqueIdentifier'}
                value={formData.uniqueIdentifier}
                onChange={formDataHandler}
                dataTestId={`value-${formData.uniqueIdentifier}`}
              />
            </div>
            {/* )} */}
          </div>
          {processVariables?.length > 0 &&
            processVariables?.map((variable, index) => (
              <div key={`${variable} - ${index}`}>
                <label className="font-medium" data-testid={`input-${index}`}>
                  {Object.keys(variable)[0].toUpperCase()}
                </label>
                <Input
                  type="text"
                  className="w-1/2"
                  name={`value-${index}`}
                  value={Object.values(variable)[0]}
                  onChange={(e) => processDataHandler(e, index)}
                  dataTestId={`value-${index}`}
                />
              </div>
            ))}
          {tableHeader.length > 0 && (
            <div className="overflow-x-auto max-w-full">
              <table className="min-w-full table-auto border-separate border-spacing-0 shadow-md rounded-lg overflow-hidden">
                <thead className="sticky top-0 z-10">
                  <tr>
                    {tableHeader?.map((head, index) => (
                      <th
                        key={head}
                        className={`${index !== tableHeader.length - 1 ? 'border-r border-white/35' : ''} px-4 py-3 text-left text-sm font-extrabold tracking-wide bg-primary_bg_color text-white`}>
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {tableBody?.length > 0 ? (
                    tableBody?.map((body, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {tableHeader?.map((head) => (
                          <td key={`${rowIndex} - ${head}`} className="px-4 py-2 border-b">
                            {body[head]}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={tableHeader.length} className="px-4 py-2 text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="primary" type="submit" className=" mr-4" disabled={isLoading}>
              {btnTilte}
            </Button>
            <Button
              variant="secondary"
              onClick={closeModalHandler}
              data-testid="cancel-btn"
              className="flex justify-between items-center gap-3 whitespace-nowrap !h-10">
              {`Cancel`}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddDataSourceModal;
